import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Typography,
  Box,
} from '@material-ui/core';
import { IMaskInput } from 'react-imask';
import { makeStyles } from '@material-ui/core/styles';
import { WorkspacesService } from '../../services/WorkspacesService';
import { Environment, NewWorkspaceCommand, WorkspacePurpose } from './types';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingTop: theme.spacing(2),
    boxSizing: 'border-box',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formControl: {
    width: '100%',
  },
  sectionTitle: {
    fontWeight: 600,
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: 12,
  },
}));

export interface NewWorkspaceFormData {
  purpose: WorkspacePurpose;
  totvsCode: string;
  adminEmail: string;
  documentType?: DocumentType;
  document: string;
  commercialName: string;
  country: string;
}

interface NewWorkspaceDialogProps {
  open: boolean;
  environment: Environment;
  onClose: () => void;
  workspacesService: WorkspacesService;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  onCreated?: () => void;
}

type DocumentType = 'CPF' | 'CNPJ';

const COUNTRY_CONFIG: Array<{ name: string; iso: string; language: string }> = [
  { name: 'Brazil', iso: 'BR', language: 'pt-br' },
  { name: 'Argentina', iso: 'AR', language: 'es-ar' },
  { name: 'Mexico', iso: 'MX', language: 'es-mx' },
  { name: 'Colombia', iso: 'CO', language: 'es-co' },
  { name: 'United States', iso: 'US', language: 'en-us' },
  { name: 'Switzerland', iso: 'CH', language: 'de-ch' },
  { name: 'United Kingdom', iso: 'GB', language: 'en-gb' },
];

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const isValidCPF = (cpf: string): boolean => {
  const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
  return cpfRegex.test(cpf.trim());
};

const isValidCNPJ = (cnpj: string): boolean => {
  const cnpjRegex =
    /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/;
  return cnpjRegex.test(cnpj.trim());
};

const getDocumentMask = (documentType?: DocumentType): string => {
  if (documentType === 'CPF') return '000.000.000-00';
  if (documentType === 'CNPJ') return 'AA.AAA.AAA/AAAA-00';
  return '';
};

const getDocumentMaskDefinitions = (documentType?: DocumentType) => {
  if (documentType === 'CNPJ') {
    return {
      A: {
        mask: /[A-Za-z0-9]/,
      },
    };
  }
  return undefined;
};

export const NewWorkspaceDialog = ({
  open,
  environment,
  onClose,
  workspacesService,
  showSuccess,
  showError,
  onCreated,
}: NewWorkspaceDialogProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [searchingAccount, setSearchingAccount] = useState(false);

  const [form, setForm] = useState<NewWorkspaceFormData>({
    purpose: WorkspacePurpose.INTERNAL,
    totvsCode: '',
    adminEmail: '',
    documentType: undefined,
    document: '',
    commercialName: '',
    country: '',
  });

  const [touched, setTouched] = useState<
    Record<keyof NewWorkspaceFormData, boolean>
  >({
    purpose: false,
    totvsCode: false,
    adminEmail: false,
    documentType: false,
    document: false,
    commercialName: false,
    country: false,
  });

  const handleChange =
    (field: keyof NewWorkspaceFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
        const value = e.target.value as string;
        setForm(prev => ({ ...prev, [field]: value }));
        setSubmitError(null);
      };

  const handleDocumentChange = (value: string) => {
    const formattedValue = form.documentType === 'CNPJ' ? value.toUpperCase() : value;
    setForm(prev => ({ ...prev, document: formattedValue }));
    setSubmitError(null);
  };

  const handleBlur = (field: keyof NewWorkspaceFormData) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  useEffect(() => {
    if (form.purpose === WorkspacePurpose.INTERNAL) {
      setForm(prev => ({ ...prev, totvsCode: 'SUPPLY' }));
    } else if (form.purpose === WorkspacePurpose.TRIAL) {
      setForm(prev => ({ ...prev, totvsCode: 'PROSPECT' }));
    } else {
      setForm(prev => ({ ...prev, totvsCode: '' }));
    }
  }, [form.purpose]);

  useEffect(() => {
    if (form.country !== 'Brazil') {
      setForm(prev => ({ ...prev, documentType: undefined }));
    }

    setForm(prev => ({ ...prev, document: '' }));
  }, [form.documentType, form.country]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchAccount = async () => {
      if (!form.totvsCode.trim() || loading) return;

      setSearchingAccount(true);
      try {
        const response = await workspacesService.searchAccounts(environment, {
          search: form.totvsCode.trim(),
        });

        if (response?.items && response.items.length > 0) {
          const account = response.items[0];
          const countryName = COUNTRY_CONFIG.find(c => c.iso === account.country)?.name ?? 'Brazil';

          setForm(prev => ({
            ...prev,
            commercialName: prev.commercialName || account.name || '',
            document: prev.document || account.document || '',
            country: prev.country || countryName,
          }));
        } else {
          setForm(prev => ({ ...prev, country: prev.country || 'Brazil' }));
        }
      } catch (err) {
        console.error('Error fetching account by totvsCode:', err);
        setForm(prev => ({ ...prev, country: prev.country || 'Brazil' }));
      } finally {
        setSearchingAccount(false);
      }
    };

    timeoutId = setTimeout(searchAccount, 500);
    return () => clearTimeout(timeoutId);
  }, [form.totvsCode, environment, workspacesService, loading]);

  const isFieldEmpty = (value: string) => !value || value.trim() === '';

  const errors = useMemo(() => {
    const baseErrors = {
      purpose: isFieldEmpty(form.purpose) ? 'Purpose is required' : '',
      totvsCode: isFieldEmpty(form.totvsCode) ? 'Totvs Code is required' : '',
      commercialName: isFieldEmpty(form.commercialName)
        ? 'Commercial Name is required'
        : '',
      country: isFieldEmpty(form.country) ? 'Country is required' : '',
      document: isFieldEmpty(form.document) ? 'Document is required' : '',
    };

    const emailError = isFieldEmpty(form.adminEmail)
      ? 'Admin Email is required'
      : !isValidEmail(form.adminEmail)
        ? 'Invalid email'
        : '';

    const cpfError = isFieldEmpty(form.document)
      ? ''
      : !isValidCPF(form.document)
        ? 'CPF inválido'
        : '';

    const cnpjError = isFieldEmpty(form.document)
      ? ''
      : !isValidCNPJ(form.document)
        ? 'CNPJ inválido'
        : '';

    return {
      ...baseErrors,
      adminEmail: emailError,
      document:
        form.country === 'Brazil' && form.documentType
          ? form.documentType === 'CPF'
            ? cpfError
            : cnpjError
          : '',
    };
  }, [form]);

  const isFormValid =
    !errors.purpose &&
    !errors.totvsCode &&
    !errors.adminEmail &&
    !errors.commercialName &&
    !errors.country &&
    !errors.document;

  const hasDocumentType = !(form.country === 'Brazil' && !form.documentType);

  const resetState = () => {
    setForm({
      purpose: WorkspacePurpose.INTERNAL,
      totvsCode: '',
      adminEmail: '',
      documentType: undefined,
      document: '',
      commercialName: '',
      country: '',
    });
    setTouched({
      purpose: false,
      totvsCode: false,
      adminEmail: false,
      documentType: false,
      document: false,
      commercialName: false,
      country: false,
    });
    setSubmitError(null);
    setSearchingAccount(false);
  };

  const handleClose = () => {
    if (loading || searchingAccount) return;
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    setTouched({
      purpose: true,
      totvsCode: true,
      adminEmail: true,
      documentType: true,
      document: true,
      commercialName: true,
      country: true,
    });

    if (!isFormValid) return;

    setLoading(true);
    setSubmitError(null);

    const selectedCountry = COUNTRY_CONFIG.find(c => c.name === form.country);
    const documentData = {
      value: form.document.trim(),
      country: selectedCountry?.iso ?? 'BR',
    };

    const command: NewWorkspaceCommand = {
      purpose: form.purpose,
      totvsCode: form.totvsCode.trim(),
      name: form.commercialName.trim(),
      email: form.adminEmail.trim(),
      document: documentData,
      language: selectedCountry?.language ?? 'pt-br',
      catalogData: {
        system: {
          id: 'eng001',
          ref: 'system:supply-engenharia/access',
          name: 'Access',
        }
      }
    };

    try {
      await workspacesService.newWorkspaceProvision(environment, command);
      showSuccess('Workspace created successfully!');
      if (onCreated) onCreated();
      handleClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Error creating workspace. Please try again.';
      setSubmitError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>New Workspace</DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.row}>
          <FormControl
            variant="outlined"
            className={classes.formControl}
            size="small"
          >
            <InputLabel id="purpose-label">Purpose</InputLabel>
            <Select
              labelId="purpose-label"
              label="Purpose"
              value={form.purpose}
              onChange={handleChange('purpose')}
              onBlur={handleBlur('purpose')}
              disabled={loading || searchingAccount}
            >
              <MenuItem value={WorkspacePurpose.INTERNAL}>
                Internal
              </MenuItem>
              <MenuItem value={WorkspacePurpose.TRIAL}>
                Trial
              </MenuItem>
              <MenuItem value={WorkspacePurpose.TRAINING}>
                Training
              </MenuItem>
              <MenuItem value={WorkspacePurpose.HOMOLOG}>
                Homologation
              </MenuItem>
              <MenuItem value={WorkspacePurpose.PRODUCTION}>
                Production
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Totvs Code"
            variant="outlined"
            fullWidth
            size="small"
            value={form.totvsCode}
            onChange={handleChange('totvsCode')}
            onBlur={handleBlur('totvsCode')}
            error={touched.totvsCode && !!errors.totvsCode}
            helperText={
              touched.totvsCode && errors.totvsCode
                ? errors.totvsCode
                : searchingAccount
                  ? 'Fetching account...'
                  : undefined
            }
            disabled={loading || form.purpose === WorkspacePurpose.INTERNAL || form.purpose === WorkspacePurpose.TRIAL}
            InputProps={{
              endAdornment: searchingAccount ? (
                <CircularProgress size={20} />
              ) : null,
            }}
          />
        </div>

        <Typography className={classes.sectionTitle}>
          Workspace Data
        </Typography>
        <Divider className={classes.divider} />

        <div className={classes.row}>
          <TextField
            label="Commercial Name"
            variant="outlined"
            fullWidth
            size="small"
            value={form.commercialName}
            onChange={handleChange('commercialName')}
            onBlur={handleBlur('commercialName')}
            error={touched.commercialName && !!errors.commercialName}
            helperText={touched.commercialName && errors.commercialName}
            disabled={loading || searchingAccount}
          />

          <TextField
            label="Admin Email"
            variant="outlined"
            fullWidth
            size="small"
            value={form.adminEmail}
            onChange={handleChange('adminEmail')}
            onBlur={handleBlur('adminEmail')}
            error={touched.adminEmail && !!errors.adminEmail}
            helperText={touched.adminEmail && errors.adminEmail}
            disabled={loading || searchingAccount}
          />
        </div>

        <div className={classes.row}>
          <FormControl
            variant="outlined"
            className={classes.formControl}
            size="small"
          >
            <InputLabel id="country-label">Country</InputLabel>
            <Select
              labelId="country-label"
              label="Country"
              value={form.country}
              onChange={handleChange('country')}
              onBlur={handleBlur('country')}
              disabled={loading || searchingAccount}
            >
              {COUNTRY_CONFIG.map(c => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.country === 'Brazil' && (
            <FormControl
              variant="outlined"
              className={classes.formControl}
              size="small"
              disabled={loading || searchingAccount}
            >
              <InputLabel id="document-type-label">Document Type</InputLabel>
              <Select
                labelId="document-type-label"
                label="Document Type"
                value={form.documentType ?? ''}
                onChange={handleChange('documentType')}
              >
                <MenuItem value="CPF">CPF</MenuItem>
                <MenuItem value="CNPJ">CNPJ</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            placeholder={getDocumentMask(form.documentType)}
            label="Document"
            variant="outlined"
            fullWidth
            className={classes.formControl}
            size="small"
            value={form.document}
            onChange={form.documentType === undefined ? handleChange('document') : undefined}
            onBlur={handleBlur('document')}
            error={touched.document && !!errors.document}
            helperText={touched.document && errors.document}
            disabled={loading || searchingAccount || !hasDocumentType}
            InputProps={{
              inputComponent: IMaskInput as any,
              inputProps: {
                mask: getDocumentMask(form.documentType),
                onAccept: handleDocumentChange,
                definitions: getDocumentMaskDefinitions(form.documentType),
              },
            }}
          />
        </div>

        {submitError && (
          <Box className={classes.errorMessage}>{submitError}</Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading || searchingAccount}>
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || searchingAccount || !isFormValid}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
